import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { apiService } from '../../../services';
import { utils } from '../../../utils';
import FormLayout from '../../../components/Common/FormLayout';

interface CorrectiveMaintenanceFormData {
  veiculo_id: number | '';
  veiculo_placa: string;
  descricao: string;
  tipo_problema: string;
  sintomas: string;
  causa_raiz: string;
  solucao_aplicada: string;
  data_solicitacao: string;
  data_inicio: string;
  data_conclusao: string;
  custo_estimado: number | '';
  custo_real: number | '';
  custo_pecas: number | '';
  custo_mao_obra: number | '';
  fornecedor_nome: string;
  fornecedor_cnpj: string;
  status: string;
  prioridade: string;
  tempo_total: number | '';
  veiculo_parado: boolean;
  responsavel: string;
  observacoes: string;
}

// Componente dual-mode: serve tanto para CADASTRAR (rota /novo, sem id)
// quanto para EDITAR (rota /editar/:id). O modo é definido pela presença do id na URL.
const CorrectiveMaintenanceCreate: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams(); // string | undefined -> presente somente no modo edição
  const isEdit = Boolean(id); // true quando estamos editando um registro existente
  const [veiculos, setVeiculos] = useState<any[]>([]);
  const [formData, setFormData] = useState<CorrectiveMaintenanceFormData>({
    veiculo_id: '',
    veiculo_placa: '',
    descricao: '',
    tipo_problema: '',
    sintomas: '',
    causa_raiz: '',
    solucao_aplicada: '',
    data_solicitacao: '',
    data_inicio: '',
    data_conclusao: '',
    custo_estimado: '',
    custo_real: '',
    custo_pecas: '',
    custo_mao_obra: '',
    fornecedor_nome: '',
    fornecedor_cnpj: '',
    status: 'Solicitada',
    prioridade: 'Média',
    tempo_total: '',
    veiculo_parado: false,
    responsavel: '',
    observacoes: ''
  });
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string | undefined>>({});

  useEffect(() => {
    loadVehicles();
  }, []);

  // No modo edição, carrega o registro existente e preenche o formData.
  useEffect(() => {
    if (isEdit) {
      loadMaintenance();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const loadVehicles = async () => {
    try {
      const response = await apiService.getVehicles({ page: 1, limit: 100 });
      if (response.success && response.data) {
        setVeiculos(response.data || []);
      }
    } catch (error) {
      console.error('Erro ao carregar veículos:', error);
    }
  };

  // Converte um TIMESTAMP do backend (ISO) para o formato do input datetime-local ("YYYY-MM-DDTHH:mm").
  const toDateTimeLocal = (value?: string | null): string => {
    if (!value) return '';
    const date = new Date(value);
    if (isNaN(date.getTime())) return '';
    // Ajusta para o fuso local antes de fatiar, evitando deslocamento de horário.
    const offsetMs = date.getTimezoneOffset() * 60000;
    return new Date(date.getTime() - offsetMs).toISOString().slice(0, 16);
  };

  // Normaliza números que podem vir como string (DECIMAL do PostgreSQL) ou nulos.
  // Mantém '' para os campos number | '' quando o valor for nulo/indefinido.
  const toNumberOrEmpty = (value: any): number | '' => {
    if (value === null || value === undefined || value === '') return '';
    const num = Number(value);
    return isNaN(num) ? '' : num;
  };

  // Carrega o registro no modo edição e mapeia cada campo da API para o formData.
  const loadMaintenance = async () => {
    if (!id) return;
    try {
      const response = await apiService.getCorrectiveMaintenance(Number(id));
      if (response.success && response.data) {
        const d = response.data;
        setFormData({
          veiculo_id: toNumberOrEmpty(d.veiculo_id),
          veiculo_placa: d.veiculo_placa || '',
          descricao: d.descricao || '',
          tipo_problema: d.tipo_problema || '',
          sintomas: d.sintomas || '',
          causa_raiz: d.causa_raiz || '',
          solucao_aplicada: d.solucao_aplicada || '',
          data_solicitacao: toDateTimeLocal(d.data_solicitacao),
          data_inicio: toDateTimeLocal(d.data_inicio),
          data_conclusao: toDateTimeLocal(d.data_conclusao),
          custo_estimado: toNumberOrEmpty(d.custo_estimado),
          custo_real: toNumberOrEmpty(d.custo_real),
          custo_pecas: toNumberOrEmpty(d.custo_pecas),
          custo_mao_obra: toNumberOrEmpty(d.custo_mao_obra),
          fornecedor_nome: d.fornecedor_nome || '',
          fornecedor_cnpj: d.fornecedor_cnpj || '',
          status: d.status || 'Solicitada',
          prioridade: d.prioridade || 'Média',
          tempo_total: toNumberOrEmpty(d.tempo_total),
          veiculo_parado: Boolean(d.veiculo_parado),
          responsavel: d.responsavel || '',
          observacoes: d.observacoes || ''
        });
      } else {
        utils.showNotification('Manutenção corretiva não encontrada', { type: 'error' });
        navigate('/manutencoes-corretivas');
      }
    } catch (error: any) {
      console.error('Erro ao carregar manutenção corretiva:', error);
      utils.showNotification(error.message || 'Erro ao carregar manutenção corretiva', { type: 'error' });
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;

    // Handle vehicle selection
    if (name === 'veiculo_id' && value) {
      const selectedVehicle = veiculos.find(v => v.id === parseInt(value));
      setFormData(prev => ({
        ...prev,
        veiculo_id: parseInt(value),
        veiculo_placa: selectedVehicle?.placa || ''
      }));
    }
    // Handle checkbox
    else if (type === 'checkbox') {
      setFormData(prev => ({
        ...prev,
        [name]: (e.target as HTMLInputElement).checked
      }));
    }
    else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: undefined
      }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.descricao.trim()) {
      newErrors.descricao = 'Descrição é obrigatória';
    }

    if (!formData.tipo_problema.trim()) {
      newErrors.tipo_problema = 'Tipo de problema é obrigatório';
    }

    if (!formData.sintomas.trim()) {
      newErrors.sintomas = 'Sintomas são obrigatórios';
    }

    if (!formData.data_solicitacao) {
      newErrors.data_solicitacao = 'Data de solicitação é obrigatória';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      utils.showNotification('Por favor, corrija os erros no formulário', { type: 'error' });
      return;
    }

    setIsLoading(true);
    try {
      const submitData = {
        veiculo_id: formData.veiculo_id || undefined,
        veiculo_placa: formData.veiculo_placa || undefined,
        descricao: formData.descricao,
        tipo_problema: formData.tipo_problema,
        sintomas: formData.sintomas,
        causa_raiz: formData.causa_raiz || undefined,
        solucao_aplicada: formData.solucao_aplicada || undefined,
        data_solicitacao: formData.data_solicitacao,
        data_inicio: formData.data_inicio || undefined,
        data_conclusao: formData.data_conclusao || undefined,
        custo_estimado: formData.custo_estimado === '' ? undefined : Number(formData.custo_estimado),
        custo_real: formData.custo_real === '' ? undefined : Number(formData.custo_real),
        custo_pecas: formData.custo_pecas === '' ? undefined : Number(formData.custo_pecas),
        custo_mao_obra: formData.custo_mao_obra === '' ? undefined : Number(formData.custo_mao_obra),
        fornecedor_nome: formData.fornecedor_nome || undefined,
        fornecedor_cnpj: formData.fornecedor_cnpj || undefined,
        status: formData.status,
        prioridade: formData.prioridade,
        tempo_total: formData.tempo_total === '' ? undefined : Number(formData.tempo_total),
        veiculo_parado: formData.veiculo_parado,
        responsavel: formData.responsavel || undefined,
        observacoes: formData.observacoes || undefined
      };

      // Modo dual: no modo edição chama o update; caso contrário, mantém o create.
      const response = isEdit
        ? await apiService.updateCorrectiveMaintenance(Number(id), submitData)
        : await apiService.createCorrectiveMaintenance(submitData);

      if (response.success) {
        utils.showNotification(
          isEdit ? 'Manutenção corretiva atualizada com sucesso!' : 'Manutenção corretiva cadastrada com sucesso!',
          { type: 'success' }
        );
        navigate('/manutencoes-corretivas');
      } else {
        throw new Error(response.message || (isEdit ? 'Erro ao atualizar manutenção corretiva' : 'Erro ao cadastrar manutenção corretiva'));
      }
    } catch (error: any) {
      console.error(isEdit ? 'Erro ao atualizar manutenção corretiva:' : 'Erro ao cadastrar manutenção corretiva:', error);
      utils.showNotification(error.message || (isEdit ? 'Erro ao atualizar manutenção corretiva' : 'Erro ao cadastrar manutenção corretiva'), { type: 'error' });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSave = () => {
    const fakeEvent = {
      preventDefault: () => {}
    } as React.FormEvent;
    handleSubmit(fakeEvent);
  };

  const handleCancel = () => {
    navigate('/manutencoes-corretivas');
  };

  return (
    <FormLayout
      title={isEdit ? 'Editar Manutenção Corretiva' : 'Nova Manutenção Corretiva'}
      subtitle={isEdit ? 'Edição de Manutenção Corretiva' : 'Cadastro de Manutenção Corretiva'}
      onCancel={handleCancel}
      onSave={handleSave}
      saveButtonText={isEdit ? 'Salvar Alterações' : 'Cadastrar Manutenção'}
      isLoading={isLoading}
      showActions={false}
    >
      <form onSubmit={handleSubmit}>
        {/* Informações Básicas */}
        <div style={{ marginBottom: '30px' }}>
          <h3 style={{ marginBottom: '20px', color: '#2c3e50', borderBottom: '2px solid #04C64F', paddingBottom: '10px' }}>
            Informações Básicas
          </h3>

          <div className="info-grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))' }}>
            <div className="form-group">
              <label className="form-label">Veículo</label>
              <select
                name="veiculo_id"
                value={formData.veiculo_id}
                onChange={handleInputChange}
                className="form-control"
              >
                <option value="">Selecione um veículo (opcional)</option>
                {veiculos.map(veiculo => (
                  <option key={veiculo.id} value={veiculo.id}>
                    {veiculo.placa} - {veiculo.marca} {veiculo.modelo}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label className="form-label">Placa do Veículo (texto)</label>
              <input
                type="text"
                name="veiculo_placa"
                value={formData.veiculo_placa}
                onChange={handleInputChange}
                className="form-control"
                placeholder="Ex: ABC-1234"
                maxLength={20}
              />
            </div>

            <div className="form-group">
              <label className="form-label">Descrição *</label>
              <input
                type="text"
                name="descricao"
                value={formData.descricao}
                onChange={handleInputChange}
                className={`form-control ${errors.descricao ? 'error' : ''}`}
                placeholder="Ex: Reparo no sistema de freios"
              />
              {errors.descricao && <span className="error-message">{errors.descricao}</span>}
            </div>

            <div className="form-group">
              <label className="form-label">Tipo de Problema *</label>
              <input
                type="text"
                name="tipo_problema"
                value={formData.tipo_problema}
                onChange={handleInputChange}
                className={`form-control ${errors.tipo_problema ? 'error' : ''}`}
                placeholder="Ex: Freios, Elétrico, Motor, etc."
                maxLength={100}
              />
              {errors.tipo_problema && <span className="error-message">{errors.tipo_problema}</span>}
            </div>

            <div className="form-group">
              <label className="form-label">Status</label>
              <select
                name="status"
                value={formData.status}
                onChange={handleInputChange}
                className="form-control"
              >
                <option value="Solicitada">Solicitada</option>
                <option value="Em Diagnóstico">Em Diagnóstico</option>
                <option value="Em Andamento">Em Andamento</option>
                <option value="Aguardando Peças">Aguardando Peças</option>
                <option value="Concluída">Concluída</option>
                <option value="Cancelada">Cancelada</option>
              </select>
            </div>

            <div className="form-group">
              <label className="form-label">Prioridade</label>
              <select
                name="prioridade"
                value={formData.prioridade}
                onChange={handleInputChange}
                className="form-control"
              >
                <option value="Baixa">Baixa</option>
                <option value="Média">Média</option>
                <option value="Alta">Alta</option>
                <option value="Urgente">Urgente</option>
                <option value="Crítica">Crítica</option>
              </select>
            </div>

            <div className="form-group" style={{ display: 'flex', alignItems: 'center', paddingTop: '25px' }}>
              <label className="form-label" style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', margin: 0 }}>
                <input
                  type="checkbox"
                  name="veiculo_parado"
                  checked={formData.veiculo_parado}
                  onChange={handleInputChange}
                  style={{ marginRight: '10px', width: '18px', height: '18px', cursor: 'pointer' }}
                />
                Veículo Parado
              </label>
            </div>
          </div>
        </div>

        {/* Diagnóstico */}
        <div style={{ marginBottom: '30px' }}>
          <h3 style={{ marginBottom: '20px', color: '#2c3e50', borderBottom: '2px solid #04C64F', paddingBottom: '10px' }}>
            Diagnóstico
          </h3>

          <div className="form-group" style={{ marginBottom: '20px' }}>
            <label className="form-label">Sintomas *</label>
            <textarea
              name="sintomas"
              value={formData.sintomas}
              onChange={handleInputChange}
              className={`form-control ${errors.sintomas ? 'error' : ''}`}
              rows={3}
              placeholder="Descreva os sintomas apresentados pelo veículo..."
            />
            {errors.sintomas && <span className="error-message">{errors.sintomas}</span>}
          </div>

          <div className="form-group" style={{ marginBottom: '20px' }}>
            <label className="form-label">Causa Raiz</label>
            <textarea
              name="causa_raiz"
              value={formData.causa_raiz}
              onChange={handleInputChange}
              className="form-control"
              rows={3}
              placeholder="Qual foi a causa raiz do problema identificado..."
            />
          </div>

          <div className="form-group">
            <label className="form-label">Solução Aplicada</label>
            <textarea
              name="solucao_aplicada"
              value={formData.solucao_aplicada}
              onChange={handleInputChange}
              className="form-control"
              rows={3}
              placeholder="Descreva a solução aplicada para resolver o problema..."
            />
          </div>
        </div>

        {/* Datas e Tempo */}
        <div style={{ marginBottom: '30px' }}>
          <h3 style={{ marginBottom: '20px', color: '#2c3e50', borderBottom: '2px solid #04C64F', paddingBottom: '10px' }}>
            Datas e Tempo
          </h3>

          <div className="info-grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))' }}>
            <div className="form-group">
              <label className="form-label">Data de Solicitação *</label>
              <input
                type="datetime-local"
                name="data_solicitacao"
                value={formData.data_solicitacao}
                onChange={handleInputChange}
                className={`form-control ${errors.data_solicitacao ? 'error' : ''}`}
              />
              {errors.data_solicitacao && <span className="error-message">{errors.data_solicitacao}</span>}
            </div>

            <div className="form-group">
              <label className="form-label">Data de Início</label>
              <input
                type="datetime-local"
                name="data_inicio"
                value={formData.data_inicio}
                onChange={handleInputChange}
                className="form-control"
              />
            </div>

            <div className="form-group">
              <label className="form-label">Data de Conclusão</label>
              <input
                type="datetime-local"
                name="data_conclusao"
                value={formData.data_conclusao}
                onChange={handleInputChange}
                className="form-control"
              />
            </div>

            <div className="form-group">
              <label className="form-label">Tempo Total (horas)</label>
              <input
                type="number"
                name="tempo_total"
                value={formData.tempo_total}
                onChange={handleInputChange}
                className="form-control"
                placeholder="0.0"
                min="0"
                step="0.1"
              />
            </div>

            <div className="form-group">
              <label className="form-label">Responsável</label>
              <input
                type="text"
                name="responsavel"
                value={formData.responsavel}
                onChange={handleInputChange}
                className="form-control"
                placeholder="Ex: Mecânico Carlos"
                maxLength={100}
              />
            </div>
          </div>
        </div>

        {/* Custos */}
        <div style={{ marginBottom: '30px' }}>
          <h3 style={{ marginBottom: '20px', color: '#2c3e50', borderBottom: '2px solid #04C64F', paddingBottom: '10px' }}>
            Custos
          </h3>

          <div className="info-grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))' }}>
            <div className="form-group">
              <label className="form-label">Custo Estimado (R$)</label>
              <input
                type="number"
                name="custo_estimado"
                value={formData.custo_estimado}
                onChange={handleInputChange}
                className="form-control"
                placeholder="0.00"
                min="0"
                step="0.01"
              />
            </div>

            <div className="form-group">
              <label className="form-label">Custo Real (R$)</label>
              <input
                type="number"
                name="custo_real"
                value={formData.custo_real}
                onChange={handleInputChange}
                className="form-control"
                placeholder="0.00"
                min="0"
                step="0.01"
              />
            </div>

            <div className="form-group">
              <label className="form-label">Custo de Peças (R$)</label>
              <input
                type="number"
                name="custo_pecas"
                value={formData.custo_pecas}
                onChange={handleInputChange}
                className="form-control"
                placeholder="0.00"
                min="0"
                step="0.01"
              />
            </div>

            <div className="form-group">
              <label className="form-label">Custo de Mão de Obra (R$)</label>
              <input
                type="number"
                name="custo_mao_obra"
                value={formData.custo_mao_obra}
                onChange={handleInputChange}
                className="form-control"
                placeholder="0.00"
                min="0"
                step="0.01"
              />
            </div>
          </div>
        </div>

        {/* Fornecedor */}
        <div style={{ marginBottom: '30px' }}>
          <h3 style={{ marginBottom: '20px', color: '#2c3e50', borderBottom: '2px solid #04C64F', paddingBottom: '10px' }}>
            Fornecedor
          </h3>

          <div className="info-grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))' }}>
            <div className="form-group">
              <label className="form-label">Nome do Fornecedor</label>
              <input
                type="text"
                name="fornecedor_nome"
                value={formData.fornecedor_nome}
                onChange={handleInputChange}
                className="form-control"
                placeholder="Ex: Mecânica Central"
                maxLength={200}
              />
            </div>

            <div className="form-group">
              <label className="form-label">CNPJ do Fornecedor</label>
              <input
                type="text"
                name="fornecedor_cnpj"
                value={formData.fornecedor_cnpj}
                onChange={handleInputChange}
                className="form-control"
                placeholder="00.000.000/0000-00"
                maxLength={18}
              />
            </div>
          </div>
        </div>

        {/* Observações */}
        <div style={{ marginBottom: '30px' }}>
          <h3 style={{ marginBottom: '20px', color: '#2c3e50', borderBottom: '2px solid #04C64F', paddingBottom: '10px' }}>
            Observações
          </h3>

          <div className="form-group">
            <label className="form-label">Observações Gerais</label>
            <textarea
              name="observacoes"
              value={formData.observacoes}
              onChange={handleInputChange}
              className="form-control"
              rows={4}
              placeholder="Informações adicionais sobre a manutenção corretiva..."
            />
          </div>
        </div>

        {/* Botões de Ação */}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '30px', paddingTop: '20px', borderTop: '1px solid #e0e0e0' }}>
          <button
            type="button"
            className="btn btn-secondary"
            onClick={handleCancel}
            disabled={isLoading}
          >
            <i className="fas fa-times"></i>
            Cancelar
          </button>
          <button
            type="submit"
            className="btn btn-primary"
            disabled={isLoading}
          >
            {isLoading ? (
              <>
                <i className="fas fa-spinner fa-spin"></i>
                {isEdit ? 'Salvando...' : 'Cadastrando...'}
              </>
            ) : (
              <>
                <i className="fas fa-save"></i>
                {isEdit ? 'Salvar Alterações' : 'Cadastrar Manutenção'}
              </>
            )}
          </button>
        </div>
      </form>
    </FormLayout>
  );
};

export default CorrectiveMaintenanceCreate;
